import { ConditionGroup, NextPageParams } from "@/schema"
import {
  InsurancePoliciesResponse,
  InsurancePolicy,
  InsurancePolicyExtractionResponse,
  InsurancePolicyInput,
} from "@/schema/insurance-policy"

import { ApiContext, apiContextFetch } from "../base"
import { appendConditionGroupParams, appendViewSortParams } from "../condition-group-params"
import { signedIdsForUploadedFiles } from "../direct-upload-files"

export function fetchInsurancePolicies(
  context: ApiContext,
  conditionGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
): Promise<InsurancePoliciesResponse> {
  const params = new URLSearchParams()
  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }
  if (searchTerm) params.append("search", searchTerm)
  if (conditionGroup) appendConditionGroupParams(params, conditionGroup)
  appendViewSortParams(params, viewSort)

  return apiContextFetch(context, `insurance_policies?${params.toString()}`)
}

export function fetchHouseholdInsurancePolicies(
  context: ApiContext,
  householdId: string
): Promise<InsurancePoliciesResponse> {
  return apiContextFetch(context, `clients/${householdId}/insurance_policies`)
}

export function fetchInsurancePolicy(
  context: ApiContext,
  householdId: string,
  policyId: string
): Promise<InsurancePolicy> {
  return apiContextFetch(context, `clients/${householdId}/insurance_policies/${policyId}`)
}

export function createInsurancePolicy(
  context: ApiContext,
  householdId: string,
  input: InsurancePolicyInput,
  newFiles: File[] = []
): Promise<InsurancePolicy> {
  return persistInsurancePolicy(context, `clients/${householdId}/insurance_policies`, "POST", input, newFiles)
}

export function updateInsurancePolicy(
  context: ApiContext,
  householdId: string,
  policyId: string,
  input: Partial<InsurancePolicyInput>
): Promise<InsurancePolicy> {
  return apiContextFetch(context, `clients/${householdId}/insurance_policies/${policyId}`, {
    method: "PUT",
    body: JSON.stringify({ insurance_policy: input }),
  })
}

export async function updateInsurancePolicyWithFiles(
  context: ApiContext,
  householdId: string,
  policyId: string,
  newFiles: File[],
  existingSignedIds: string[] = []
): Promise<InsurancePolicy> {
  const files = await signedIdsForUploadedFiles(context, newFiles, existingSignedIds)

  return apiContextFetch(context, `clients/${householdId}/insurance_policies/${policyId}`, {
    method: "PUT",
    body: JSON.stringify({ insurance_policy: { files } }),
  })
}

async function persistInsurancePolicy(
  context: ApiContext,
  path: string,
  method: "POST",
  input: InsurancePolicyInput,
  newFiles: File[]
): Promise<InsurancePolicy> {
  const files = newFiles.length ? await signedIdsForUploadedFiles(context, newFiles) : undefined

  return apiContextFetch(context, path, {
    method,
    body: JSON.stringify({ insurance_policy: { ...input, ...(files ? { files } : {}) } }),
  })
}

export function deleteInsurancePolicy(context: ApiContext, householdId: string, policyId: string): Promise<void> {
  return apiContextFetch(context, `clients/${householdId}/insurance_policies/${policyId}`, { method: "DELETE" })
}

export function extractInsurancePolicyStatement(
  context: ApiContext,
  householdId: string,
  file: File
): Promise<InsurancePolicyExtractionResponse> {
  const formData = new FormData()
  formData.append("file", file)

  return apiContextFetch(context, `clients/${householdId}/insurance_policy_extractions`, {
    method: "POST",
    body: formData,
    headers: { "Content-Type": undefined as never },
  })
}

export function fetchTrustInsurancePolicies(context: ApiContext, trustId: string): Promise<InsurancePoliciesResponse> {
  return apiContextFetch(context, `trusts/${trustId}/insurance_policies`)
}
